import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  InputAdornment,
  Divider,
  Tab,
  Tabs,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import EventIcon from '@mui/icons-material/Event';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { fireStoreDB } from 'services';
import { getProfile } from 'utilities';
import { addTrip, getTripsByCompany, deleteTrip, getUsersByCompany } from 'services/firebaseServices';
import { calcScore, scoreColor, scoreBand, missingCriticalLabels } from '../dutyOfCare';
import { tripRiskLevel, RISK_META, matchTraveller } from '../travelRisk';

// ── Book Flights ──────────────────────────────────────────────────────────────
function BookFlights() {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [tripType, setTripType] = useState('return');

  const handleSearch = () => {
    const from = encodeURIComponent(origin || 'anywhere');
    const to = encodeURIComponent(destination || 'anywhere');
    const depart = departDate.replace(/-/g, '') || '';
    const ret = returnDate.replace(/-/g, '') || '';
    const pax = passengers;
    const url = tripType === 'return'
      ? `https://www.skyscanner.net/transport/flights/${from}/${to}/${depart}/${ret}/?adults=${pax}&cabinclass=economy`
      : `https://www.skyscanner.net/transport/flights/${from}/${to}/${depart}/?adults=${pax}&cabinclass=economy`;
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>Search Flights</Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>Search and compare flights for your employees. Results open in Skyscanner.</Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Trip type</InputLabel>
              <Select value={tripType} label="Trip type" onChange={(e) => setTripType(e.target.value)}>
                <MenuItem value="return">Return</MenuItem>
                <MenuItem value="oneway">One way</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Passengers" type="number"
              value={passengers} onChange={(e) => setPassengers(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: 50 }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="From (city or airport code)"
              value={origin} onChange={(e) => setOrigin(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><FlightTakeoffIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment> }}
              placeholder="e.g. Oslo, LHR, CPH"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="To (city or airport code)"
              value={destination} onChange={(e) => setDestination(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><FlightLandIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment> }}
              placeholder="e.g. Barcelona, JFK, DXB"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Departure date" type="date"
              value={departDate} onChange={(e) => setDepartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><EventIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment> }}
            />
          </Grid>
          {tripType === 'return' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Return date" type="date"
                value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <InputAdornment position="start"><EventIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment> }}
              />
            </Grid>
          )}
        </Grid>

        <Button
          variant="contained" size="large" endIcon={<OpenInNewIcon />}
          onClick={handleSearch}
          disabled={!origin || !destination || !departDate}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, px: 4 }}
        >
          Search Flights on Skyscanner
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', bgcolor: '#f8faff' }}>
        <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
          <strong style={{ color: '#1f2937' }}>Coming soon:</strong> Direct flight search and booking integration. Once connected, you will be able to search, compare, and book flights for your employees without leaving the dashboard.
        </Typography>
      </Paper>
    </Box>
  );
}

// ── Upcoming Travel ───────────────────────────────────────────────────────────
function UpcomingTravel() {
  const [businessData, setBusinessData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ employee: '', from: '', to: '', depart: '', return: '', status: 'confirmed' });
  const [record, setRecord] = useState(null); // trip selected for the record dialog

  useEffect(() => {
    const load = async () => {
      try {
        const profile = getProfile();
        if (!profile?.uid) { setLoading(false); return; }
        const snap = await getDocs(query(collection(fireStoreDB, 'businesses'), where('userId', '==', profile.uid)));
        if (!snap.empty) {
          const biz = snap.docs[0].data();
          setBusinessData(biz);
          const [emps, t] = await Promise.all([
            getUsersByCompany(biz.companyName),
            getTripsByCompany(biz.companyName),
          ]);
          setEmployees(emps);
          setTrips(t);
        }
      } catch (e) {
        console.error('Error loading trips:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAddTrip = async () => {
    if (!form.employee || !form.from || !form.to || !form.depart || !businessData?.companyName) return;
    setSaving(true);
    const saved = await addTrip({ ...form, company: businessData.companyName });
    if (saved) {
      setTrips((prev) => [...prev, saved]);
      setForm({ employee: '', from: '', to: '', depart: '', return: '', status: 'confirmed' });
      setShowForm(false);
    }
    setSaving(false);
  };

  const handleRemoveTrip = async (id) => {
    const ok = await deleteTrip(id);
    if (ok) setTrips((prev) => prev.filter((t) => t.id !== id));
  };

  const statusColor = (s) => (s === 'confirmed' ? 'success' : s === 'pending' ? 'warning' : 'default');

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #EAECF0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#101828' }}>Upcoming Trips</Typography>
            <Typography variant="body2" sx={{ color: '#667085' }}>Each trip gets a risk level and a Duty of Care record.</Typography>
          </Box>
          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => setShowForm(!showForm)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#32ABF8', '&:hover': { bgcolor: '#1E91D6' } }}
          >
            Add Trip
          </Button>
        </Box>

        {showForm && (
          <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #CDE9FB', bgcolor: '#F5FBFF', mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#101828', mb: 2 }}>New Trip</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {employees.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Employee</InputLabel>
                    <Select
                      label="Employee"
                      value={form.employee}
                      onChange={(e) => setForm({ ...form, employee: e.target.value })}
                    >
                      {employees.map((emp) => {
                        const name = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                        return <MenuItem key={emp.id} value={name}>{name}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField fullWidth size="small" label="Employee name" value={form.employee} onChange={(e) => setForm({ ...form, employee: e.target.value })} />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={form.status} label="Status" onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="From (city / airport)" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="To (country / city)" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Departure" type="date" InputLabelProps={{ shrink: true }} value={form.depart} onChange={(e) => setForm({ ...form, depart: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Return" type="date" InputLabelProps={{ shrink: true }} value={form.return} onChange={(e) => setForm({ ...form, return: e.target.value })} /></Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="contained" size="small" onClick={handleAddTrip} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#32ABF8' }}>
                {saving ? 'Saving…' : 'Save Trip'}
              </Button>
              <Button variant="outlined" size="small" onClick={() => setShowForm(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : trips.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FlightTakeoffIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary">No upcoming trips logged yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Employee', 'Route', 'Departure', 'Return', 'Risk', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, bgcolor: '#F9FAFB', color: '#667085', border: 'none', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {trips.map((trip) => {
                  const lvl = tripRiskLevel(trip.to);
                  const meta = RISK_META[lvl];
                  return (
                    <TableRow key={trip.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontWeight: 600, color: '#101828' }}>{trip.employee}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ color: '#475467' }}>{trip.from}</Typography>
                          <FlightTakeoffIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                          <Typography variant="body2" sx={{ color: '#475467' }}>{trip.to}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#475467' }}>{trip.depart}</TableCell>
                      <TableCell sx={{ color: '#475467' }}>{trip.return || '—'}</TableCell>
                      <TableCell>
                        <Chip label={meta.label} size="small" sx={{ fontWeight: 700, bgcolor: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}55` }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={trip.status} color={statusColor(trip.status)} size="small" sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Duty of Care record" arrow>
                          <IconButton size="small" onClick={() => setRecord(trip)} sx={{ color: '#32ABF8' }}>
                            <DescriptionIcon sx={{ fontSize: 19 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove trip" arrow>
                          <IconButton size="small" onClick={() => handleRemoveTrip(trip.id)} sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' } }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <TripRecordDialog trip={record} employees={employees} company={businessData?.companyName} onClose={() => setRecord(null)} />
    </Box>
  );
}

// ── Per-trip Duty of Care record ──────────────────────────────────────────────
function TripRecordDialog({ trip, employees, company, onClose }) {
  if (!trip) return null;
  const lvl = tripRiskLevel(trip.to);
  const meta = RISK_META[lvl];
  const traveller = matchTraveller(trip, employees);
  const readiness = traveller ? calcScore(traveller) : null;
  const band = readiness !== null ? scoreBand(readiness) : null;
  const criticalMissing = traveller ? missingCriticalLabels(traveller) : [];

  const Line = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #F2F4F7' }}>
      <Typography variant="body2" sx={{ color: '#667085' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: '#101828', fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );

  return (
    <Dialog open={!!trip} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#EAF6FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HealthAndSafetyIcon sx={{ color: '#32ABF8' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#101828', lineHeight: 1.2 }}>Trip Duty of Care record</Typography>
            <Typography variant="caption" sx={{ color: '#667085' }}>{company || 'Company'} · {trip.employee}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: 2, bgcolor: `${meta.color}12`, border: `1px solid ${meta.color}40`, mb: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#667085' }}>Destination risk</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: meta.color, lineHeight: 1.2 }}>{meta.label}</Typography>
          </Box>
          <Chip label={`${trip.from} → ${trip.to}`} sx={{ fontWeight: 600, bgcolor: '#fff', border: '1px solid #EAECF0' }} />
        </Box>

        <Line label="Traveller" value={trip.employee} />
        <Line label="Departure" value={trip.depart || '—'} />
        <Line label="Return" value={trip.return || '—'} />
        <Line label="Booking status" value={<span style={{ textTransform: 'capitalize' }}>{trip.status}</span>} />

        <Box sx={{ mt: 2.5, mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#98A2B3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Traveller readiness
          </Typography>
        </Box>
        {traveller ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: '#F9FAFB' }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={readiness}
                  sx={{ height: 8, borderRadius: 4, bgcolor: '#EAECF0', '& .MuiLinearProgress-bar': { bgcolor: scoreColor(readiness), borderRadius: 4 } }} />
              </Box>
              <Chip label={`${readiness}% · ${band.label}`} sx={{ fontWeight: 700, bgcolor: `${band.color}18`, color: band.color }} />
            </Box>
            {criticalMissing.length > 0 && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#FEF3F2', border: '1px solid #FEE4E2', display: 'flex', gap: 1, alignItems: 'center' }}>
                <WarningAmberIcon sx={{ color: '#D92D20', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#B42318', fontWeight: 500 }}>
                  Before departure, resolve: {criticalMissing.join(', ')}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: '#98A2B3' }}>
            Traveller not matched to an employee profile — add them as an employee to track readiness.
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: '#98A2B3' }}>
          Record generated {new Date().toLocaleString()} · aligned to ISO 31030 travel-risk assessment.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

// ── Travel page (default export) ──────────────────────────────────────────────
export default function TravelPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        borderBottom: 1, borderColor: 'divider', bgcolor: 'white',
        borderRadius: '12px 12px 0 0', border: '1px solid #e5e7eb',
        borderBottomColor: 'divider', px: 2,
        mx: { xs: 1, md: 2 }, mt: { xs: 1, md: 2 },
      }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, color: '#6b7280' },
            '& .Mui-selected': { color: '#2563eb', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: '#2563eb' },
          }}
        >
          <Tab label="Book Flights" />
          <Tab label="Upcoming Travel" />
        </Tabs>
      </Box>
      {tab === 0 && <BookFlights />}
      {tab === 1 && <UpcomingTravel />}
    </Box>
  );
}
